import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { AuthLayout } from '../components/AuthLayout'
import { Button, ButtonLink, FormError } from '../components/ui'
import { ApiError, api, call, messageOf } from '../lib/api'
import { inviteQuery, meQuery } from '../lib/queries'

// An invite link. Signed out: straight to sign-up with the code attached.
// Signed in: one tap to join.
export const Route = createFileRoute('/join/$code')({
  beforeLoad: async ({ context, params }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery)
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) throw redirect({ to: '/signup', search: { invite: params.code, next: undefined } })
      throw e
    }
  },
  loader: ({ context, params }) => context.queryClient.ensureQueryData(inviteQuery(params.code)),
  component: Join,
  errorComponent: () => (
    <AuthLayout title="That link has expired" lede="Invite links can be reset by the league admin. Ask for a fresh one.">
      <ButtonLink to="/" variant="primary" block>
        Go home
      </ButtonLink>
    </AuthLayout>
  ),
})

function Join() {
  const { code } = Route.useParams()
  const { data: invite } = useSuspenseQuery(inviteQuery(code))
  const navigate = useNavigate()
  const qc = useQueryClient()
  const join = useMutation({
    mutationFn: () => call(api.POST('/leagues/join', { body: { invite_code: code } })),
    onSuccess: (league) => {
      qc.invalidateQueries({ queryKey: meQuery.queryKey })
      navigate({ to: '/l/$slug', params: { slug: league.slug } })
    },
  })
  return (
    <AuthLayout title={`Join ${invite.league_name}`} lede={`${invite.admin_name} runs it. ${invite.member_count} ${invite.member_count === 1 ? 'manager' : 'managers'} so far.`}>
      <FormError>{join.isError && messageOf(join.error)}</FormError>
      <Button variant="primary" block busy={join.isPending} onClick={() => join.mutate()}>
        Join the league
      </Button>
    </AuthLayout>
  )
}
